import { DatePipe, TitleCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { compareDateStrings, isWithinTimeFrameInDays } from 'src/app/core/api/date.util';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { PampIframeService } from 'src/app/core/services/pamp-iframe.service';
import {
  DashboardNumberCardStatConfig,
  StatisticsConfig,
  TimeFrameInDays,
  timeFrameToCardConfigMap
} from '../dashboard.model';
import { PatientAppStatusColor } from 'src/app/core/models/patient-app-status-color';
import { Patient } from 'src/API';
import { OrderRequest, Product } from 'src/app/core/models/order-request.model';
import {
  CheckinData,
  WidgetCardConfig,
  WidgetCardTemplate,
  DashboardPatient,
  PatientAppTimelineEvent
} from 'src/app/core/models/patient-app-widget.model';
import { PatientAppWidgetDataService } from 'src/app/core/api/patient-app-widget-data.service';

@Component({
  selector: 'csi-patient-app-widgets',
  templateUrl: './patient-app-widgets.component.html',
  styleUrls: ['./patient-app-widgets.component.scss'],
  providers: [DatePipe, TitleCasePipe]
})
export class PatientAppWidgetsComponent implements OnInit, OnDestroy {
  public readonly translocoFileKey = 'dashboard.';

  // Widget Data Lists
  public allProductOrders: Partial<OrderRequest>[] = [];
  public completedProductOrders: Partial<OrderRequest>[] = [];
  public invitedPatients: DashboardPatient[] = [];
  public awaitingInvitationPatients: DashboardPatient[] = [];
  public recentlyActivePatients: DashboardPatient[] = [];
  public recentlyCompletedCheckins: CheckinData[] = [];

  // Statistics Cards
  statsCards: {
    [title: string]: {
      title: string;
      cardBottomColor: string;
      tooltip?: string;
      showComparison: boolean;
      multiStatConfig: DashboardNumberCardStatConfig[];
    };
  } = {};

  statsConfig: StatisticsConfig[] = [
    {
      serviceName: 'PatientApp',
      functionName: 'allProductOrders',
      params: [],
      title: 'Product Orders Received',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'orange',
      filterBy: 'requestDate',
      tooltip:
        'Represents the total number of product orders received from patients, including pending and completed orders. Determined by the initial request date.',
      showComparison: true
    },
    {
      serviceName: 'PatientApp',
      functionName: 'completedProductOrders',
      params: [],
      title: 'Product Orders Completed',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'orange',
      filterBy: 'processedAt',
      tooltip:
        'Represents the number of product orders that have been successfully processed. Determined by the date the order was completed.'
    },
    {
      serviceName: 'PatientApp',
      functionName: 'invitedPatients',
      params: [],
      title: 'App Invitations Sent',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'green',
      filterBy: 'patientAppInvitedDate',
      tooltip:
        'Represents the number of patients who have been successfully invited to the app. Determined by the date they were invited.'
    },
    {
      serviceName: 'PatientApp',
      functionName: 'awaitingInvitation',
      params: [],
      title: 'App Invitations Pending',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'green',
      filterBy: 'createdAt',
      tooltip: 'Represents patients who were never invited to the patient app.',
      showComparison: false
    },
    {
      serviceName: 'PatientApp',
      functionName: 'activePatients',
      params: [],
      title: 'Active Patients On App',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'blue',
      filterBy: 'patientAppLastActivityDate',
      tooltip:
        'Represents patients who have logged in or interacted with the app recently. Determined by their last recorded activity date.',
      showComparison: false
    },
    {
      serviceName: 'PatientApp',
      functionName: 'completedCheckins',
      params: [],
      title: 'Dry Eye Check-ins',
      timeFrameSelection: [
        TimeFrameInDays.WEEK,
        TimeFrameInDays.MONTH,
        TimeFrameInDays.QUARTER,
        TimeFrameInDays.YEAR,
        TimeFrameInDays.ALL
      ],
      cardBottomColor: 'blue',
      filterBy: 'completedAt',
      tooltip:
        'Represents the total number of dry eye check-ins completed by patients. Determined by the date the check-in was finished.',
      showComparison: false
    }
  ];

  // Table Widget Cards
  widgetCards: WidgetCardConfig[] = [];

  private readonly cardTemplates: WidgetCardTemplate[] = [
    {
      id: 'allProductOrders',
      title: 'Product Orders Received',
      colour: 'orange'
    },
    {
      id: 'recentlyCompletedProductOrders',
      title: 'Product Orders Completed',
      colour: 'orange'
    },
    {
      id: 'invitedPatients',
      title: 'App Invitations Sent',
      colour: 'green'
    },
    {
      id: 'awaitingInvitation',
      title: 'App Invitations Pending',
      colour: 'green'
    },
    {
      id: 'recentlyActivePatients',
      title: 'Active Patients On App',
      colour: 'blue'
    },
    {
      id: 'recentlyCompletedCheckins',
      title: 'Dry Eye Check-ins',
      colour: 'blue'
    }
  ];

  constructor(
    public router: Router,
    private datePipe: DatePipe,
    private titleCasePipe: TitleCasePipe,
    public pampIframeService: PampIframeService,
    public appStateService: AppStateService,
    private patientAppWidgetDataService: PatientAppWidgetDataService
  ) {}

  ngOnInit() {
    this.appStateService.isPatientAppActive$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(isActive => {
        if (isActive) {
          this.loadPatientAppData();
        }
      });

    this.pampIframeService.iframeClosed$
      .pipe(withLatestFrom(this.appStateService.isPatientAppActive$), untilDestroyed(this))
      .subscribe(([_, isActive]) => {
        if (isActive) {
          this.loadPatientAppData();
        }
      });

    this.patientAppWidgetDataService.data$.pipe(untilDestroyed(this)).subscribe(data => {
      this.processPatientData(data.patients || []);
      this.processProductOrders(data.productOrderStats);
      this.processCheckins(data.checkinStats);
      this.generateStatisticsCards();
      this.widgetCards = this.generateWidgetCards();
    });
  }

  ngOnDestroy() {}

  public trackByStatTitle(index: number, config: StatisticsConfig): string {
    return config.title;
  }

  public trackByCardId(index: number, card: WidgetCardConfig): string {
    return card.id;
  }

  private loadPatientAppData() {
    this.patientAppWidgetDataService.loadData();
  }

  private processPatientData(patients: Patient[]) {
    const parsedPatients: DashboardPatient[] = (patients || []).map(patient => {
      const p: DashboardPatient = { ...patient };
      const config =
        typeof p.patientAppEnrollmentConfig === 'string'
          ? safeJsonParse(p.patientAppEnrollmentConfig)
          : p.patientAppEnrollmentConfig;

      p.patientAppEnrollmentConfig = config;
      const status = config && config.status ? String(config.status).toLowerCase() : null;
      const timeline = config && Array.isArray(config.timeline) ? config.timeline : [];

      const statusUpdatedAt = config && config.statusUpdatedAt ? config.statusUpdatedAt : null;
      const invitedDate = this.getInvitedDate(timeline) || statusUpdatedAt;
      const activeEventDate = this.getActiveDate(timeline);
      const isActiveStatus = status === 'active' || Boolean(activeEventDate);
      const lastActivityDate =
        activeEventDate || (isActiveStatus ? statusUpdatedAt || p.updatedAt || p.createdAt : null);

      const isInvited = Boolean(p.patientAppId);
      const rawStatus =
        config && config.status ? config.status : isInvited ? 'invited' : 'not invited';

      p.patientAppStatus = rawStatus;
      p.patientAppInvitedDate = invitedDate;
      p.patientAppLastActivityDate = lastActivityDate;

      return Object.assign(p, { isInvited, isActiveStatus, lastActivityDate });
    });
    this.invitedPatients = parsedPatients
      .filter(p => p.isInvited)
      .sort((a, b) => compareDateStrings(b.patientAppInvitedDate, a.patientAppInvitedDate));

    this.awaitingInvitationPatients = parsedPatients
      .filter(p => !p.isInvited)
      .sort((a, b) => compareDateStrings(b.createdAt, a.createdAt));
    this.recentlyActivePatients = parsedPatients
      .filter(p => p.isInvited && p.isActiveStatus && p.lastActivityDate)
      .sort((a, b) =>
        compareDateStrings(b.patientAppLastActivityDate, a.patientAppLastActivityDate)
      );
  }

  private processProductOrders(orderStats: {
    allOrders?: Partial<OrderRequest>[];
    pendingOrders?: Partial<OrderRequest>[];
    completedOrders?: Partial<OrderRequest>[];
  }) {
    if (orderStats) {
      let all: Partial<OrderRequest>[] = [];
      if (orderStats.allOrders) {
        all = orderStats.allOrders;
      } else if (orderStats.pendingOrders || orderStats.completedOrders) {
        all = [...(orderStats.pendingOrders || []), ...(orderStats.completedOrders || [])];
      }

      const completed = all.filter(order => (order.status || '').toLowerCase() === 'completed');

      this.allProductOrders = all.sort((a, b) => compareDateStrings(b.requestDate, a.requestDate));
      this.completedProductOrders = completed.sort((a, b) =>
        compareDateStrings(b.processedAt, a.processedAt)
      );
    } else {
      this.allProductOrders = [];
      this.completedProductOrders = [];
    }
  }

  private processCheckins(checkinStats: CheckinData[]) {
    if (Array.isArray(checkinStats) && checkinStats.length > 0) {
      this.recentlyCompletedCheckins = checkinStats.sort((a, b) =>
        compareDateStrings(b.completedAt || b.createdAt, a.completedAt || a.createdAt)
      );
    } else {
      this.recentlyCompletedCheckins = [];
    }
  }

  private getInvitedDate(timeline: PatientAppTimelineEvent[]): string | null {
    if (!timeline || timeline.length === 0) return null;

    const invitedEvent = timeline.find(
      item => item && item.event && item.event.toLowerCase() === 'invited'
    );
    if (invitedEvent) return invitedEvent.timestamp;

    const registeredEvent = timeline.find(
      item => item && item.event && item.event.toLowerCase() === 'registered'
    );
    if (registeredEvent) return registeredEvent.timestamp;

    const enrollEvent = timeline.find(
      item => item && item.event && item.event.toLowerCase() === 'enrolled'
    );
    return enrollEvent ? enrollEvent.timestamp : null;
  }

  private getActiveDate(timeline: PatientAppTimelineEvent[]): string | null {
    if (!timeline || timeline.length === 0) return null;
    const activeEvents = timeline.filter(
      item => item && item.event && item.event.toLowerCase() === 'active'
    );
    if (activeEvents.length === 0) return null;
    const sorted = [...activeEvents].sort((a, b) => {
      const dateA = new Date(a.timestamp || 0).getTime();
      const dateB = new Date(b.timestamp || 0).getTime();
      return dateB - dateA;
    });
    return sorted[0] ? sorted[0].timestamp || null : null;
  }

  private generateStatisticsCards() {
    this.statsConfig.forEach(config => {
      let data: any[] = [];
      switch (config.functionName) {
        case 'allProductOrders':
          data = this.allProductOrders;
          break;
        case 'completedProductOrders':
          data = this.completedProductOrders;
          break;
        case 'invitedPatients':
          data = this.invitedPatients;
          break;
        case 'awaitingInvitation':
          data = this.awaitingInvitationPatients;
          break;
        case 'activePatients':
          data = this.recentlyActivePatients;
          break;
        case 'completedCheckins':
          data = this.recentlyCompletedCheckins;
          break;
      }

      this.statsCards[config.title] = {
        title: config.title,
        cardBottomColor: config.cardBottomColor,
        tooltip: config.tooltip,
        showComparison: config.showComparison !== false,
        multiStatConfig: this.generateMultiDateRangeConfig(
          data,
          config.filterBy,
          config.timeFrameSelection || []
        )
      };
    });
  }

  private generateMultiDateRangeConfig(
    data: any[],
    filterBy: string,
    timeFrameSelection: TimeFrameInDays[] = []
  ): DashboardNumberCardStatConfig[] {
    const dateToday = new Date();
    const multiDateRangeConfig: DashboardNumberCardStatConfig[] = [];

    if (timeFrameSelection.length > 0) {
      for (const timeFrame of timeFrameSelection) {
        const startDate = new Date(dateToday.getTime() - timeFrame * 2 * 86400000);

        const dataFilteredByLastTimeFrame = (data || []).filter(value => {
          if (!value || !value[filterBy]) return false;
          const valDate = new Date(value[filterBy]);
          if (isNaN(valDate.getTime())) return false;
          return isWithinTimeFrameInDays(startDate, valDate, timeFrame);
        });

        const dateFilteredByCurrentTimeFrame = (data || []).filter(value => {
          if (!value || !value[filterBy]) return false;
          const valDate = new Date(value[filterBy]);
          if (isNaN(valDate.getTime())) return false;
          return isWithinTimeFrameInDays(dateToday, valDate, timeFrame);
        });

        const stat = dateFilteredByCurrentTimeFrame.length;
        const statChange =
          dateFilteredByCurrentTimeFrame.length - dataFilteredByLastTimeFrame.length;
        const statPercentChanged =
          ((dateFilteredByCurrentTimeFrame.length - dataFilteredByLastTimeFrame.length) /
            dataFilteredByLastTimeFrame.length) *
          100;

        multiDateRangeConfig.push({
          label: timeFrameToCardConfigMap[timeFrame].label,
          stat: String(stat),
          statChange,
          statPercentChanged,
          subtitle: timeFrameToCardConfigMap[timeFrame].subtitle
        });
      }
    }
    return multiDateRangeConfig;
  }

  private concatName(val: { firstName?: string; lastName?: string }): string {
    if (!val) return '';
    const first = val.firstName ? val.firstName.split(' ')[0] : '';
    const last = val.lastName || '';
    return this.titleCasePipe.transform(`${first} ${last}`.trim());
  }

  public formatProductNames(products: Product[] | string): string {
    if (!products) return '-';
    if (Array.isArray(products)) {
      return this.joinProductNames(products);
    }
    if (typeof products === 'string') {
      try {
        const parsed = JSON.parse(products);
        if (Array.isArray(parsed)) {
          return this.joinProductNames(parsed);
        }
      } catch (e) {
        return products;
      }
    }
    return String(products);
  }

  private joinProductNames(products: Product[]): string {
    const names = products.map(p => p && p.productName).filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  }

  public getOrderStatusLabel(status: string): string {
    if (!status) return 'Pending';
    const s = String(status).toUpperCase();
    const map: { [key: string]: string } = {
      PENDING: 'Pending',
      ACCEPTED: 'Accepted',
      IN_PROGRESS: 'In Progress',
      SHIPPED: 'Shipped',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled'
    };
    return map[s] || status;
  }

  public getOrderStatusBadgeClass(status: string): string {
    if (!status) return 'badge-orange';
    const s = String(status).toUpperCase();
    switch (s) {
      case 'COMPLETED':
        return 'badge-green';
      case 'ACCEPTED':
      case 'IN_PROGRESS':
      case 'SHIPPED':
        return 'badge-blue';
      case 'CANCELLED':
        return 'badge-red';
      case 'PENDING':
      default:
        return 'badge-orange';
    }
  }

  public getPatientAppStatusBadgeClass(status: string): string {
    if (!status) return 'badge-gray';
    const color = PatientAppStatusColor[status.toLowerCase()] || 'gray';
    return `badge-${color}`;
  }

  private onProductOrderClick(val: Partial<OrderRequest>) {
    if (!val || !val.id) return;

    const params: Record<string, string> = {
      patientId: val.patientId,
      orderId: val.id
    };

    if (
      val.status &&
      (String(val.status).toUpperCase() === 'COMPLETED' ||
        String(val.status).toUpperCase() === 'CANCELLED')
    ) {
      params.history = 'true';
    }

    this.pampIframeService.openProductRefillRequests(params);
  }

  private generateWidgetCards(): WidgetCardConfig[] {
    return this.cardTemplates.map(template => {
      switch (template.id) {
        case 'allProductOrders':
          return this.buildAllProductOrdersCard(template);
        case 'recentlyCompletedProductOrders':
          return this.buildCompletedProductOrdersCard(template);
        case 'invitedPatients':
          return this.buildInvitedPatientsCard(template);
        case 'awaitingInvitation':
          return this.buildAwaitingInvitationCard(template);
        case 'recentlyActivePatients':
          return this.buildActivePatientsCard(template);
        case 'recentlyCompletedCheckins':
          return this.buildCheckinsCard(template);
        default:
          return {
            ...template,
            expandedTitle: template.title,
            content: [],
            expandedContent: [],
            data: []
          };
      }
    });
  }

  private orderStatusBadge(status: string): string {
    return `<span class="status-chip-badge ${this.getOrderStatusBadgeClass(
      status
    )}">${this.getOrderStatusLabel(status)}</span>`;
  }

  private patientAppStatusBadge(status: string, fallback: string): string {
    return `<span class="status-chip-badge ${this.getPatientAppStatusBadgeClass(
      status
    )}">${status || fallback}</span>`;
  }

  private buildAllProductOrdersCard(
    template: WidgetCardTemplate
  ): WidgetCardConfig<Partial<OrderRequest>> {
    return {
      ...template,
      expandedTitle: 'Product Orders Received',
      content: [
        { heading: 'Name', transform: val => val.patientName || '' },
        { heading: 'Products', transform: val => this.formatProductNames(val.products) },
        {
          heading: 'Request Date',
          transform: val =>
            this.datePipe.transform(val.requestDate || val.createdAt, 'MMM dd, yyyy')
        },
        {
          heading: 'Current Order Status',
          useHTML: true,
          transform: val => this.orderStatusBadge(val.status)
        }
      ],
      expandedContent: [
        { heading: 'Name', transform: val => val.patientName || '' },
        { heading: 'Products', transform: val => this.formatProductNames(val.products) },
        {
          heading: 'Request Date',
          transform: val =>
            this.datePipe.transform(val.requestDate || val.createdAt, 'MMM dd, yyyy HH:mm')
        },
        {
          heading: 'Current Order Status',
          useHTML: true,
          transform: val => this.orderStatusBadge(val.status)
        }
      ],
      clickable: { action: val => this.onProductOrderClick(val) },
      data: this.allProductOrders
    };
  }

  private buildCompletedProductOrdersCard(
    template: WidgetCardTemplate
  ): WidgetCardConfig<Partial<OrderRequest>> {
    return {
      ...template,
      expandedTitle: 'Product Orders Completed',
      content: [
        { heading: 'Name', transform: val => val.patientName || '' },
        { heading: 'Products', transform: val => this.formatProductNames(val.products) },
        {
          heading: 'Completed On',
          transform: val => this.datePipe.transform(val.processedAt, 'MMM dd, yyyy')
        }
      ],
      expandedContent: [
        { heading: 'Name', transform: val => val.patientName || '' },
        { heading: 'Products', transform: val => this.formatProductNames(val.products) },
        {
          heading: 'Completed On',
          transform: val => this.datePipe.transform(val.processedAt, 'MMM dd, yyyy HH:mm')
        }
      ],
      clickable: { action: val => this.onProductOrderClick(val) },
      data: this.completedProductOrders
    };
  }

  private buildInvitedPatientsCard(
    template: WidgetCardTemplate
  ): WidgetCardConfig<DashboardPatient> {
    return {
      ...template,
      expandedTitle: 'App Invitations Sent',
      content: [
        { heading: 'Name', transform: val => this.concatName(val) },
        {
          heading: 'Invited On',
          transform: val => this.datePipe.transform(val.patientAppInvitedDate, 'MMM dd, yyyy')
        },
        {
          heading: 'Current App Status',
          useHTML: true,
          transform: val => this.patientAppStatusBadge(val.patientAppStatus, 'enrolled')
        }
      ],
      expandedContent: [
        { heading: 'First Name', transform: val => val.firstName },
        { heading: 'Last Name', transform: val => val.lastName },
        {
          heading: 'Invited On',
          transform: val => this.datePipe.transform(val.patientAppInvitedDate, 'MMM dd, yyyy HH:mm')
        },
        {
          heading: 'Current App Status',
          useHTML: true,
          transform: val => this.patientAppStatusBadge(val.patientAppStatus, 'enrolled')
        }
      ],
      clickable: { action: val => this.router.navigateByUrl('patients/' + val.id) },
      data: this.invitedPatients
    };
  }

  private buildAwaitingInvitationCard(
    template: WidgetCardTemplate
  ): WidgetCardConfig<DashboardPatient> {
    return {
      ...template,
      expandedTitle: 'App Invitations Pending',
      content: [
        { heading: 'Name', transform: val => this.concatName(val) },
        {
          heading: 'Created On',
          transform: val => this.datePipe.transform(val.createdAt || val.updatedAt, 'MMM dd, yyyy')
        }
      ],
      expandedContent: [
        { heading: 'First Name', transform: val => val.firstName },
        { heading: 'Last Name', transform: val => val.lastName },
        {
          heading: 'Created On',
          transform: val =>
            this.datePipe.transform(val.createdAt || val.updatedAt, 'MMM dd, yyyy HH:mm')
        }
      ],
      clickable: { action: val => this.router.navigateByUrl('patients/' + val.id) },
      data: this.awaitingInvitationPatients
    };
  }

  private buildActivePatientsCard(
    template: WidgetCardTemplate
  ): WidgetCardConfig<DashboardPatient> {
    return {
      ...template,
      expandedTitle: 'Active Patients On App',
      content: [
        { heading: 'Name', transform: val => this.concatName(val) },
        {
          heading: 'Last Active',
          transform: val =>
            this.datePipe.transform(val.patientAppLastActivityDate || val.updatedAt, 'MMM dd, yyyy')
        },
        {
          heading: 'Current App Status',
          useHTML: true,
          transform: val => this.patientAppStatusBadge(val.patientAppStatus, 'active')
        }
      ],
      expandedContent: [
        { heading: 'First Name', transform: val => val.firstName },
        { heading: 'Last Name', transform: val => val.lastName },
        {
          heading: 'Last Active',
          transform: val =>
            this.datePipe.transform(
              val.patientAppLastActivityDate || val.updatedAt,
              'MMM dd, yyyy HH:mm'
            )
        },
        {
          heading: 'Current App Status',
          useHTML: true,
          transform: val => this.patientAppStatusBadge(val.patientAppStatus, 'active')
        }
      ],
      clickable: { action: val => this.router.navigateByUrl('patients/' + val.id) },
      data: this.recentlyActivePatients
    };
  }

  private buildCheckinsCard(template: WidgetCardTemplate): WidgetCardConfig<CheckinData> {
    return {
      ...template,
      expandedTitle: 'Dry Eye Check-ins',
      content: [
        { heading: 'Name', transform: val => val.patientName || '' },
        {
          heading: 'Completed On',
          transform: val =>
            this.datePipe.transform(val.completedAt || val.createdAt, 'MMM dd, yyyy')
        }
      ],
      expandedContent: [
        { heading: 'Name', transform: val => val.patientName || '' },
        {
          heading: 'Completed On',
          transform: val =>
            this.datePipe.transform(val.completedAt || val.createdAt, 'MMM dd, yyyy HH:mm')
        }
      ],
      clickable: {
        action: val => {
          if (val.patientId) {
            this.router.navigateByUrl('patients/' + val.patientId);
          }
        }
      },
      data: this.recentlyCompletedCheckins
    };
  }
}
